import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, StatusBadge, SearchBox, TableWrap, Empty, th, td, tr } from "@/components/common";
import { ExpenseList } from "@/components/trip";

function Ledger() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState({});
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/finance/ledger").then((r) => setRows(r.data)); }, []);
  const list = rows.filter((r) => r.customer_name.toLowerCase().includes(q.toLowerCase()));
  const tot = (k) => rows.reduce((s, r) => s + r[k], 0);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard testid="ledger-total-bill" label="Total Billed" value={inr(tot("total_bill"))} />
        <StatCard testid="ledger-received" label="Received" value={inr(tot("received"))} accent="text-emerald-600" />
        <StatCard testid="ledger-outstanding" label="Outstanding" value={inr(tot("outstanding"))} accent="text-rose-600" />
      </div>
      <SearchBox testid="ledger-search-input" value={q} onChange={setQ} placeholder="Search customer…" />
      <TableWrap>
        <thead><tr>{["", "Customer", "Bookings", "Total Bill", "Received", "Outstanding"].map((h, i) => <th key={i} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {list.map((c) => (
            <Fragment key={c.customer_name}>
              <tr className={`${tr} cursor-pointer`} onClick={() => setOpen({ ...open, [c.customer_name]: !open[c.customer_name] })} data-testid={`ledger-row-${c.customer_name}`}>
                <td className={`${td} w-8`}>{open[c.customer_name] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                <td className={`${td} font-semibold`}>{c.party_id ? <Link to={`/parties/${c.party_id}`} data-testid={`ledger-party-link-${c.party_id}`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline">{c.customer_name}</Link> : c.customer_name}</td>
                <td className={td}>{c.bookings}</td>
                <td className={`${td} amount`}>{inr(c.total_bill)}</td>
                <td className={`${td} amount text-emerald-600`}>{inr(c.received)}</td>
                <td className={`${td} amount font-semibold ${c.outstanding > 0 ? "text-rose-600" : ""}`}>{inr(c.outstanding)}</td>
              </tr>
              {open[c.customer_name] && c.items.map((i) => (
                <tr key={i.id} className="border-t bg-secondary/40 text-xs">
                  <td />
                  <td className={td}><Link to={`/bookings/${i.id}`} className="font-mono font-semibold text-primary hover:underline">{i.lr_number}</Link> · {fmtDate(i.booking_date)}</td>
                  <td className={td}><StatusBadge status={i.status} /></td>
                  <td className={`${td} amount`}>{inr(i.total_bill)}</td>
                  <td className={`${td} amount`}>{inr(i.received)}</td>
                  <td className={`${td} amount`}>{inr(i.outstanding)}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </TableWrap>
      {list.length === 0 && <Empty />}
    </div>
  );
}

function PnL() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/finance/pnl").then((r) => setData(r.data)); }, []);
  const rows = useMemo(() => (data?.rows || []).filter((r) => `${r.lr_number} ${r.customer_name} ${r.vehicle_number || ""} ${r.pickup_location} ${r.drop_location}`.toLowerCase().includes(q.toLowerCase())), [data, q]);
  if (!data) return null;
  const t = data.totals;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard testid="pnl-revenue" label="Freight Revenue" value={inr(t.freight_amount)} />
        <StatCard testid="pnl-expenses" label="Trip Expenses" value={inr(t.expense_total)} accent="text-amber-600" />
        <StatCard testid="pnl-driver-cost" label="Driver Salary + KM" value={inr(t.driver_cost || 0)} accent="text-sky-600" />
        <StatCard testid="pnl-market-cost" label="Market Vehicle Hire" value={inr(t.market_cost || 0)} accent="text-violet-600" />
        <StatCard testid="pnl-profit" label="Net Profit" value={inr(t.profit)} accent="text-emerald-600" hint={t.freight_amount ? `${((t.profit / t.freight_amount) * 100).toFixed(1)}% margin` : ""} />
      </div>
      <SearchBox testid="pnl-search-input" value={q} onChange={setQ} placeholder="Search LR, customer, vehicle…" />
      <TableWrap>
        <thead><tr>{["LR", "Route", "Vehicle", "Freight", "Fuel", "Toll", "Allowance", "Maint.", "Driver Cost", "Market Hire", "Profit"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className={tr} data-testid={`pnl-row-${r.lr_number}`}>
              <td className={td}><Link to={`/bookings/${r.id}`} className="font-mono font-semibold text-primary hover:underline">{r.lr_number}</Link><div className="text-xs text-muted-foreground">{fmtDate(r.booking_date)}</div></td>
              <td className={td}>{r.pickup_location} → {r.drop_location}</td>
              <td className={`${td} font-mono text-xs`}>{r.vehicle_number || "-"}</td>
              <td className={`${td} amount`}>{inr(r.freight_amount)}</td>
              {["fuel", "toll", "allowance", "maintenance"].map((k) => <td key={k} className={`${td} amount text-muted-foreground`}>{inr(r.expense_breakdown[k] || 0)}</td>)}
              <td className={`${td} amount text-sky-600`} data-testid={`pnl-driver-cost-${r.lr_number}`}>{inr(r.driver_cost || 0)}</td>
              <td className={`${td} amount text-violet-600`} data-testid={`pnl-market-cost-${r.lr_number}`}>{inr(r.market_cost || 0)}</td>
              <td className={`${td} amount font-semibold ${r.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{inr(r.profit)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
    </div>
  );
}

function Bills() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = () => api.get("/expenses", { params: status ? { status } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  return (
    <div className="card-base p-5 md:p-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Expense Bills</h3>
        <select data-testid="bills-status-filter" className="native-select w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="">All</option>
        </select>
      </div>
      <ExpenseList expenses={items.map((e) => ({ ...e, note: `${e.lr_number}${e.note ? ` · ${e.note}` : ""}` }))} canApprove onChange={load} />
    </div>
  );
}

function Categories() {
  const { role } = useAuth();
  const [cats, setCats] = useState([]);
  const [label, setLabel] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/expense-categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    try { await api.post("/expense-categories", { label: label.trim() }); toast.success("Category added"); setLabel(""); load(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const toggle = async (c) => {
    try { await api.patch(`/expense-categories/${c.id}`, { active: !c.active }); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const rename = async () => {
    if (!newLabel.trim()) return;
    setBusy(true);
    try { await api.patch(`/expense-categories/${renaming.id}`, { label: newLabel.trim() }); toast.success("Category renamed"); setRenaming(null); load(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete category "${c.label}"?`)) return;
    try { await api.delete(`/expense-categories/${c.id}`); toast.success("Category deleted"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="card-base p-5 md:p-6">
      <h3 className="mb-1 text-lg font-semibold">Expense Categories</h3>
      <p className="mb-4 text-sm text-muted-foreground">These categories appear in the trip expense dropdown for everyone. Renaming or deactivating takes effect immediately; existing expenses keep their category.</p>
      <form onSubmit={add} className="mb-5 flex gap-2">
        <Input data-testid="cat-add-input" placeholder="New category name (e.g. AdBlue, Tyre, Brokerage)" value={label} onChange={(e) => setLabel(e.target.value)} className="max-w-sm" />
        <Button data-testid="cat-add-btn" disabled={busy || !label.trim()}>Add Category</Button>
      </form>
      <div className="divide-y" data-testid="cat-list">
        {cats.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-3 py-2.5 text-sm" data-testid={`cat-row-${c.key}`}>
            <div className="flex items-center gap-2">
              <span className={`font-medium ${c.active ? "" : "text-muted-foreground line-through"}`}>{c.label}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${c.is_default ? "bg-secondary text-muted-foreground" : "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300"}`}>{c.is_default ? "Default" : "Custom"}</span>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`cat-toggle-${c.key}`} onClick={() => toggle(c)} className={`rounded border px-2 py-0.5 text-xs font-semibold ${c.active ? "border-amber-300 text-amber-700 hover:bg-amber-50 dark:text-amber-400" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400"}`}>{c.active ? "Deactivate" : "Activate"}</button>
              <button data-testid={`cat-rename-${c.key}`} onClick={() => { setNewLabel(c.label); setRenaming(c); }} className="rounded border px-2 py-0.5 text-xs font-semibold text-muted-foreground hover:border-ring hover:text-foreground"><Pencil className="h-3 w-3" /></button>
              {role === "admin" && !c.is_default && <button data-testid={`cat-delete-${c.key}`} onClick={() => del(c)} className="rounded border border-rose-300 px-2 py-0.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 dark:text-rose-400"><Trash2 className="h-3 w-3" /></button>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!renaming} onOpenChange={(o) => { if (!o) setRenaming(null); }}>
        <DialogContent data-testid="cat-rename-dialog">
          <DialogHeader><DialogTitle>Rename category</DialogTitle><DialogDescription>Existing expenses keep this category and will show the new name.</DialogDescription></DialogHeader>
          <Input data-testid="cat-rename-input" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)}>Cancel</Button>
            <Button data-testid="cat-rename-save-btn" disabled={busy || !newLabel.trim()} onClick={rename}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Finance() {
  const { role } = useAuth();
  const canViewReports = role === "admin" || role === "accounts";
  return (
    <div>
      <PageHeader title="Finance & Accounts" subtitle={canViewReports ? "Customer ledger, trip profitability, bill approvals and expense categories" : "Expense bill approvals and categories"}>
        {canViewReports && <Button asChild variant="outline" data-testid="finance-upload-statement-btn"><Link to="/bank">Upload Bank Statement</Link></Button>}
      </PageHeader>
      <Tabs defaultValue={canViewReports ? "ledger" : "bills"}>
        <TabsList className="mb-5">
          {canViewReports && <TabsTrigger value="ledger" data-testid="finance-tab-ledger">Customer Ledger</TabsTrigger>}
          {canViewReports && <TabsTrigger value="pnl" data-testid="finance-tab-pnl">Trip P&L</TabsTrigger>}
          <TabsTrigger value="bills" data-testid="finance-tab-bills">Bill Approvals</TabsTrigger>
          <TabsTrigger value="cats" data-testid="finance-tab-cats">Expense Categories</TabsTrigger>
        </TabsList>
        {canViewReports && <TabsContent value="ledger"><Ledger /></TabsContent>}
        {canViewReports && <TabsContent value="pnl"><PnL /></TabsContent>}
        <TabsContent value="bills"><Bills /></TabsContent>
        <TabsContent value="cats"><Categories /></TabsContent>
      </Tabs>
    </div>
  );
}
